/**
 * frontend/src/pages/Dig.jsx — /dig
 * SoundDAO Sprint 6A — search the Internet Archive (Live Music Archive | Netlabels).
 * The query lives in the URL (?q=&source=) so Back returns to the same results.
 * Several tapes of one show (same artist + date, within a page) share one card with a
 * recording picker (lib/digGroups.js).
 *
 * When no recording has the query as its artist, the page says so and offers up to 3
 * "Did you mean" artists (backend lib/artistNames.js) before anything else; recordings that
 * merely mention the words sit under their own "Recordings that mention …" heading.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { digItem, digSearch, formatShowDate, founderPicks, itemToQueue } from '../lib/archiveApi';
import { usePlayer } from '../player/PlayerContext';
import ArtistChip, { NominateButton } from '../components/archive/ArtistChip';
import FounderPickTag from '../components/archive/FounderPickTag';
import { groupRecordings, recordingLabel } from '../lib/digGroups';
import { track } from '../lib/analytics';
import '../styles/archive.css';

const SOURCES = [
  { key: 'etree', label: 'Live Music' },
  { key: 'netlabels', label: 'Netlabels' },
];

function ResultCard({ group }) {
  const navigate = useNavigate();
  const { playQueue } = usePlayer();
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(0);
  const item = group.items[Math.min(sel, group.items.length - 1)];
  const recordings = group.items.length;

  const play = async () => {
    setStarting(true);
    setErr(null);
    try {
      const { item: full } = await digItem(item.identifier);
      const q = itemToQueue(full);
      if (q.length) playQueue(q, 0);
      else setErr('No playable tracks in this recording.');
    } catch (e) {
      setErr(e.message);
    } finally {
      setStarting(false);
    }
  };

  const multi = item.artists.length > 1;
  return (
    <div className="ax-card">
      <Link to={`/dig/item/${encodeURIComponent(item.identifier)}`} className="ax-title" style={{ color: '#fff', textDecoration: 'none', display: 'block' }}>
        {item.title}
      </Link>
      <div className="ax-meta">
        {[formatShowDate(item.date), item.venue, item.coverage].filter(Boolean).join(' · ')}
        {multi ? ` · ${item.artists.length} artists on this bill` : ''}
        {item.source === 'netlabels' && item.license?.licenseUrl ? ' · free license' : ''}
      </div>
      {recordings > 1 && (
        <label className="ax-meta" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <span>{recordings} recordings of this show</span>
          <select
            value={sel}
            onChange={(e) => { setSel(Number(e.target.value)); setErr(null); }}
            aria-label={`Choose a recording (${recordings} available)`}
            style={{ maxWidth: '100%', minWidth: 0, flex: '1 1 200px', background: '#111', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 6px', font: 'inherit' }}
          >
            {group.items.map((r, i) => <option key={r.identifier} value={i}>{recordingLabel(r, i)}</option>)}
          </select>
        </label>
      )}
      {item.artists.length > 0 && (
        <div className="ax-chips">{item.artists.map((a) => <ArtistChip key={a.id} artist={a} />)}</div>
      )}
      {err && <div className="ax-banner ax-banner--error">{err}</div>}
      <div className="ax-actions">
        <button type="button" className="ax-btn ax-btn--sm" onClick={play} disabled={starting}>{starting ? '…' : '▶ Play'}</button>
        <button type="button" className="ax-btn ax-btn--sm" onClick={() => navigate(`/dig/item/${encodeURIComponent(item.identifier)}`)}>Open</button>
        <NominateButton artists={item.artists} />
      </div>
    </div>
  );
}

/** One founder-curated artist in the Founder's Picks filter. */
function PickCard({ artist }) {
  return (
    <div className="ax-card dig-pick">
      <Link to={`/dig?q=${encodeURIComponent(artist.displayName)}&source=etree`} className="ax-title" style={{ color: '#fff', textDecoration: 'none', display: 'block' }}>
        {artist.displayName}
      </Link>
      <div className="ax-meta">
        {artist.shows > 0 && <>{artist.shows} show{artist.shows === 1 ? '' : 's'}{artist.span ? ` (${artist.span})` : ''}</>}
        {artist.archiveUrl && <>{artist.shows > 0 ? ' · ' : ''}<a href={artist.archiveUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#a5b4fc' }}>archive.org ↗</a></>}
      </div>
      <div className="ax-chips"><FounderPickTag /></div>
      <div className="ax-actions"><NominateButton artists={[artist]} /></div>
    </div>
  );
}

export default function Dig() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const showPicks = !q && params.get('filter') === 'founder';
  const [picks, setPicks] = useState(null);
  const source = params.get('source') === 'netlabels' ? 'netlabels' : 'etree';
  const [draft, setDraft] = useState(q);
  // groups: [{ key, items }] — one card per show; grouped per fetched page.
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [zeroCreator, setZeroCreator] = useState(false);
  // Set when the current search came from tapping a "Did you mean" chip (analytics only).
  const fromSuggestion = useRef(false);

  const load = useCallback(async (pageNo, replace) => {
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      const d = await digSearch({ q, source, page: pageNo });
      if (pageNo === 1) {
        setSuggestions(d.suggestions || []);
        setZeroCreator(Boolean(d.zeroCreatorResults));
        // Never the query text: only whether the artist search missed and how we got here.
        track('dig_search', { source, zero_creator_results: Boolean(d.zeroCreatorResults), suggestion_clicked: fromSuggestion.current });
        fromSuggestion.current = false;
      }
      setGroups((prev) => {
        // The same show can reappear on a later page; the page prefix keeps React keys unique.
        const tag = (gs) => gs.map((g) => ({ ...g, key: `p${pageNo}:${g.key}`, phase: d.phase }));
        if (replace) return tag(groupRecordings(d.items));
        const seen = new Set(prev.flatMap((g) => g.items.map((it) => it.identifier)));
        return [...prev, ...tag(groupRecordings(d.items.filter((it) => !seen.has(it.identifier))))];
      });
      setPage(pageNo);
      setHasMore(Boolean(d.hasMore));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [q, source]);

  useEffect(() => {
    setDraft(q);
    setGroups([]);
    setHasMore(false);
    setPage(1);
    setSuggestions([]);
    setZeroCreator(false);
    if (q) load(1, true);
  }, [q, source, load]);

  useEffect(() => {
    if (!showPicks) return undefined;
    let alive = true;
    setPicks(null);
    founderPicks().then((d) => alive && setPicks(d.artists || [])).catch((e) => alive && setError(e));
    return () => { alive = false; };
  }, [showPicks]);

  const submit = (e) => {
    e.preventDefault();
    const next = draft.trim().slice(0, 100);
    if (!next) return;
    setParams({ q: next, source });
  };
  const setSource = (s) => setParams(q ? { q, source: s } : { source: s });
  const pickSuggestion = (name) => {
    fromSuggestion.current = true;
    setParams({ q: name.slice(0, 100), source });
  };
  const unit = source === 'netlabels' ? 'release' : 'show';
  const firstTextKey = groups.find((g) => g.phase === 'text')?.key;

  // The backend says whether another page exists (artist matches first, then full text).
  const moreAvailable = q && hasMore;

  return (
    <div className="ax-page">
      <div className="ax-wrap">
        <h1 className="ax-h1">Dig the Archive</h1>
        <p className="ax-sub">Live tapes and free-license releases from the Internet Archive. Find a band, add them to a playlist, nominate them.</p>

        <form className="ax-search" onSubmit={submit} role="search">
          <span aria-hidden="true">🔍</span>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search bands, venues, songs…"
            aria-label="Search the Internet Archive"
            maxLength={100}
            enterKeyHint="search"
          />
          <button type="submit" className="ax-btn ax-btn--sm ax-btn--primary" disabled={!draft.trim()}>Search</button>
        </form>

        <div className="ax-seg" role="tablist" aria-label="Source">
          {SOURCES.map((s) => (
            <button key={s.key} type="button" role="tab" aria-selected={source === s.key} className={source === s.key ? 'on' : ''} onClick={() => setSource(s.key)}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="ax-chips dig-filters" style={{ margin: '8px 0 4px' }}>
          <button
            type="button"
            className={`ax-chip dig-filter-founder ${showPicks ? 'on' : ''}`}
            aria-pressed={showPicks}
            onClick={() => setParams(showPicks ? { source } : { filter: 'founder', source: 'etree' })}
            style={{ background: showPicks ? 'rgba(255,255,255,.16)' : 'transparent', cursor: 'pointer', paddingRight: 10 }}
          >
            ★ Founder&#39;s Picks
          </button>
        </div>

        {showPicks && (
          <>
            <h2 className="ax-h2">Founder&#39;s Picks</h2>
            <div className="ax-grid">
              {(picks || []).map((a) => <PickCard key={a.id} artist={a} />)}
              {picks === null && !error && [0, 1].map((i) => <div key={`pk${i}`} className="ax-skel" />)}
            </div>
            {picks && picks.length === 0 && <div className="ax-empty">No Founder&#39;s Picks yet.</div>}
          </>
        )}

        {error && (
          <div className={`ax-banner ${error.code === 'ARCHIVE_UNAVAILABLE' ? '' : 'ax-banner--error'}`}>
            <span>{error.message}</span>
            <button type="button" className="ax-btn ax-btn--sm" onClick={() => load(groups.length ? page + 1 : 1, !groups.length)}>Retry</button>
          </div>
        )}

        {!q && !showPicks && !error && (
          <div className="ax-empty">
            {source === 'etree'
              ? 'Search the Live Music Archive: 1,600+ bands that let fans tape their shows.'
              : 'Search netlabel releases under free licenses (public domain, CC0, CC BY).'}
          </div>
        )}

        {q && zeroCreator && !loading && (suggestions.length > 0 || groups.length > 0) && (
          <div className="ax-banner dig-didyoumean" role="status" style={{ display: 'block' }}>
            <span>No artist named “{q}”.</span>
            {suggestions.length > 0 && (
              <>
                {' '}Did you mean:{' '}
                <span className="ax-chips" style={{ display: 'inline-flex', marginTop: 6 }}>
                  {suggestions.map((s) => (
                    <button key={s.name} type="button" className="ax-chip dig-suggestion" onClick={() => pickSuggestion(s.name)} style={{ background: 'transparent', cursor: 'pointer', paddingRight: 10 }}>
                      {s.name}{s.count > 0 ? ` (${s.count.toLocaleString()} ${unit}${s.count === 1 ? '' : 's'})` : ''}
                    </button>
                  ))}
                </span>
              </>
            )}
          </div>
        )}

        <div className="ax-grid">
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              {g.key === firstTextKey && (
                <h2 className="ax-h2 dig-text-heading" style={{ gridColumn: '1 / -1' }}>Recordings that mention “{q}”</h2>
              )}
              <ResultCard group={g} />
            </React.Fragment>
          ))}
          {loading && [0, 1, 2, 3].map((i) => <div key={`sk${i}`} className="ax-skel" />)}
        </div>

        {q && !loading && !error && groups.length === 0 && suggestions.length === 0 && (
          <div className="ax-empty">No playable results for “{q}”. Try a band name, a city, or the other source.</div>
        )}
        {moreAvailable && !loading && groups.length > 0 && (
          <div style={{ textAlign: 'center', margin: '12px 0' }}>
            <button type="button" className="ax-btn" onClick={() => load(page + 1, false)}>Load more</button>
          </div>
        )}
        <p className="ax-attr" style={{ textAlign: 'center', marginTop: 18 }}>
          Streaming from the Internet Archive · <Link to="/playlists" style={{ color: '#a5b4fc' }}>My Playlists</Link>
        </p>
      </div>
    </div>
  );
}
